import TableData, { ColumnDefinition, tableConstants } from '../../../../components/tableView/TableData/TableData';

const arraySort = require('array-sort');

const formatter = require('../../../../utils/formatter/formatter');
const dateUtil = require('../../../../utils/dateUtil/dateUtil');
const constants = require('../../../../utils/domain/constants');
const saleCalculator = require('../../../../utils/domain/sales/saleCalculator');
const staffUtility = require('../../../../utils/domain/staff/staffUtility');
const accountsUtils = require('../../../../utils/domain/accountsUtils');
const localizationUtils = require('../../../../utils/domain/localizationUtils');
const salesLoader = require('../../../../utils/domain/sales/salesLoader');
const floatsLoader = require('../../../../utils/domain/floats/floatsLoader');
const businessDateUtil = require('../../../../utils/dateUtil/businessDateUtil');
const stateManager = require('../../../../utils/state/stateManager');

const { columnAlignments } = tableConstants;

export async function loadReportData(currentDate, forceReloading)
{
    const promises = [];

    if (businessDateUtil.isDateSameAsTodayBusinessDate(currentDate))
    {
        // For todaySales, we invoke loadTodaysSalesAndSalesStatus only on forceReloading
        // else we get data from redux store

        if (forceReloading)
        {
            promises.push(
                (async () =>
                {
                    await salesLoader.loadTodaysSalesAndSalesStatus();
                    return stateManager.getStore().getState().todaySales
                })()
            );
        }

        promises.push(Promise.resolve(stateManager.getStore().getState().todaySales));
    }
    else
    {
        promises.push(salesLoader.loadSales(currentDate));
    }

    promises.push(floatsLoader.loadFloatsByDateRange(dateUtil.subtractDays(currentDate, 1), dateUtil.addDays(currentDate, 1)));

    const [currentSales, floatSessions] = await Promise.all(promises);

    const currentFloatSessions = floatSessions.filter(floatSession =>
        currentSales.some(sale => sale.floatSessionId === floatSession.id) ||
        floatSession.endDateTime == null);

    arraySort(currentFloatSessions, "startDateTime");

    return { currentSales, currentFloatSessions };
}

export function getSalesSummaryTableData(sales, templateElement)
{
    // Overwrite title from template
    const title = templateElement.title || "Sales Summary";

    let totalSalesCount = 0;
    let totalRetailOrderPrice = 0;
    let totalDiscount = 0;
    let totalNetOrderPrice = 0;
    let totalCharges = 0;
    let totalSalePrice = 0;
    let totalVat = 0;
    let totalSalePriceIncTax = 0;
    let averageTicketSize = 0;

    let outstandingSalesCount = 0;
    let outstandingSalesAmount = 0;

    sales.forEach(sale => 
    {
        const retailOrderPrice = saleCalculator.calculateTotalRetailOrderPrice(sale);
        const discount = saleCalculator.calculateTotalDiscounts(sale);
        const netOrderPrice = retailOrderPrice - discount;
        const charges = saleCalculator.calculateTotalCharges(sale);
        const salePrice = netOrderPrice + charges;
        const vat = saleCalculator.calculateTotalOrderVAT(sale) + saleCalculator.calculateTotalChargesVAT(sale);
        const salePriceIncTax = salePrice + vat;

        totalSalesCount++;
        totalRetailOrderPrice += retailOrderPrice;
        totalDiscount += discount;
        totalNetOrderPrice += netOrderPrice;
        totalCharges += charges;
        totalSalePrice += salePrice;
        totalVat += vat;
        totalSalePriceIncTax += salePriceIncTax;

        if (!saleCalculator.isSaleFullyPaid(sale)) 
        {
            outstandingSalesCount++;
            outstandingSalesAmount += saleCalculator.calculateRemainingPayableAmount(sale);
        }
    });

    if (totalSalesCount > 0) 
    {
        averageTicketSize = totalSalePriceIncTax / totalSalesCount;
    }

    const columnDefinitions = [];
    columnDefinitions.push(new ColumnDefinition(75));
    columnDefinitions.push(new ColumnDefinition(25, columnAlignments.alignRight));

    const tableData = new TableData("salesSummary", title, ...columnDefinitions);

    let tableRow;

    tableData.addDataRow("Sales Count", totalSalesCount.toString());
    tableData.addDataRow("Average Ticket Size", formatter.convertToAmountFormat(averageTicketSize));
    tableData.addDataRow("Retail Order Price", formatter.convertToAmountFormat(totalRetailOrderPrice));
    tableData.addDataRow("Discount", formatter.convertToAmountFormat(totalDiscount));
    tableData.addDataRow("Net Order Price", formatter.convertToAmountFormat(totalNetOrderPrice));
    tableData.addDataRow("Charges", formatter.convertToAmountFormat(totalCharges));
    tableData.addDataRow("Sale Price", formatter.convertToAmountFormat(totalSalePrice));
    tableData.addDataRow("Tax", formatter.convertToAmountFormat(totalVat));

    tableRow = tableData.addDataRow("Sale Price Inclusive of Tax", formatter.convertToAmountFormat(totalSalePriceIncTax));
    tableRow.afterBlankRow = true;
    tableRow.columns[0].isBold = true;
    tableRow.columns[1].isBold = true;

    tableData.addDataRow("Outstanding Sales Count", outstandingSalesCount.toString());
    tableData.addDataRow("Outstanding Sales Amount", formatter.convertToAmountFormat(outstandingSalesAmount));

    return tableData;
}

export function getSalesByVenueCodeTableData(sales, templateElement)
{
    // Overwrite title from template
    const title = templateElement.title || "Venue Sales";

    const venueSales = [];

    sales.forEach(sale =>
    {
        let venueSale = venueSales.find(x => x.venueCode === sale.venueCode);

        if (venueSale == null) 
        {
            venueSale = { venueCode: sale.venueCode, count: 0, amount: 0.0 };
            venueSales.push(venueSale);
        }

        venueSale.count += 1;
        venueSale.amount += saleCalculator.calculateTotalSalePrice(sale);
    });

    const totalCount = venueSales.reduce((previous, current) => previous + current.count, 0);
    const totalAmount = venueSales.reduce((previous, current) => previous + current.amount, 0);

    arraySort(venueSales, "venueCode");

    const columnDefinitions = [];
    columnDefinitions.push(new ColumnDefinition(50));
    columnDefinitions.push(new ColumnDefinition(25, columnAlignments.alignRight));
    columnDefinitions.push(new ColumnDefinition(25, columnAlignments.alignRight));

    const tableData = new TableData("salesByVenueCode", title, ...columnDefinitions);

    tableData.setHeaderRow("Venue", "Count", "Amount");
    tableData.setFooterRow("Total", totalCount.toString(), formatter.convertToAmountFormat(totalAmount));

    venueSales.forEach(sale => tableData.addDataRow(sale.venueCode, sale.count.toString(), formatter.convertToAmountFormat(sale.amount)));

    return tableData;
}

export function getSalesByDispatchTypeTableData(sales, templateElement)
{
    // Overwrite title from template
    const title = templateElement.title || "Dispatch Sales";

    const dispatchSales = [];

    sales.forEach(sale =>
    {
        let dispatchSale = dispatchSales.find(x => x.dispatchType === sale.dispatchType);

        if (dispatchSale == null) 
        {
            dispatchSale = { dispatchType: sale.dispatchType, count: 0, amount: 0.0 };
            dispatchSales.push(dispatchSale);
        }

        dispatchSale.count += 1;
        dispatchSale.amount += saleCalculator.calculateTotalSalePrice(sale);
    });

    const totalCount = dispatchSales.reduce((previous, current) => previous + current.count, 0);
    const totalAmount = dispatchSales.reduce((previous, current) => previous + current.amount, 0);

    arraySort(dispatchSales, "dispatchType");

    const columnDefinitions = [];
    columnDefinitions.push(new ColumnDefinition(50));
    columnDefinitions.push(new ColumnDefinition(25, columnAlignments.alignRight));
    columnDefinitions.push(new ColumnDefinition(25, columnAlignments.alignRight));

    const tableData = new TableData("salesByDispatchType", title, ...columnDefinitions);

    tableData.setHeaderRow("Dispatch Type", "Count", "Amount");
    tableData.setFooterRow("Total", totalCount.toString(), formatter.convertToAmountFormat(totalAmount));

    dispatchSales.forEach(sale => tableData.addDataRow(sale.dispatchType, sale.count.toString(), formatter.convertToAmountFormat(sale.amount)));

    return tableData;
}

export function getSalesByPaymentMethodTableData(sales, templateElement)
{
    // Overwrite title from template
    const title = templateElement.title || "Payment Method Sales";

    const paymentSales = [];

    let outStandingSales = 0.0;

    sales.forEach(sale =>
    {
        const receiptLines = sale.receiptLines.filter(receiptLine => receiptLine.type === "Charge");

        if (receiptLines.length > 0)
        {
            receiptLines.forEach(line => 
            {
                let paymentSale = paymentSales.find(x => x.paymentMethod === line.paymentMethod);

                if (paymentSale == null) 
                {
                    paymentSale = { paymentMethod: sale.paymentMethod, amount: 0.0 };
                    paymentSales.push(paymentSale);
                }

                paymentSale.amount += line.amount;
            });
        }

        if (!saleCalculator.isSaleFullyPaid(sale))
        {
            outStandingSales += saleCalculator.calculateRemainingPayableAmount(sale);
        }
    });

    paymentSales.push({ paymentMethod: "Outstanding Sales", amount: outStandingSales });

    const totalAmount = paymentSales.reduce((previous, current) => previous + current.amount, 0);

    arraySort(paymentSales, "paymentMethod");

    const columnDefinitions = [];
    columnDefinitions.push(new ColumnDefinition(75));
    columnDefinitions.push(new ColumnDefinition(25, columnAlignments.alignRight));

    const tableData = new TableData("salesByPaymentMethod", title, ...columnDefinitions);

    tableData.setHeaderRow("Payment Method", "Amount");
    tableData.setFooterRow("Total", formatter.convertToAmountFormat(totalAmount));

    paymentSales.forEach(sale => tableData.addDataRow(sale.paymentMethod, formatter.convertToAmountFormat(sale.amount)));

    return tableData;
}

export function getSalesByCoversTableData(sales, templateElement)
{
    // Overwrite title from template
    const title = templateElement.title || "Covers Sales Summary";

    const { totalNumberOfGuests, totalSalePriceIncTax } = saleCalculator.calculateTotalCoversSales(sales);
    const averageRevenuePerCover = totalNumberOfGuests > 0 ? (totalSalePriceIncTax / totalNumberOfGuests) : 0;

    const columnDefinitions = [];
    columnDefinitions.push(new ColumnDefinition(75));
    columnDefinitions.push(new ColumnDefinition(25, columnAlignments.alignRight));

    const tableData = new TableData("salesByCovers", title, ...columnDefinitions);

    tableData.addDataRow("Total number of covers", totalNumberOfGuests.toString());
    tableData.addDataRow("Average revenue per cover", formatter.convertToAmountFormat(averageRevenuePerCover));

    return tableData;
}

export function getSaleDiscountsTableData(sales, templateElement)
{
    // Overwrite title from template
    const title = templateElement.title || "Sale Discounts";

    const saleDiscounts = [];

    sales.forEach(sale =>
    {
        if (sale.discounts.length === 0) return;

        sale.discounts.forEach(discount =>
        {
            let saleDiscount = saleDiscounts.find(x => x.type === discount.type);

            if (saleDiscount == null) 
            {
                saleDiscount = { type: discount.type, count: 0, amount: 0.0 };
                saleDiscounts.push(saleDiscount);
            }

            saleDiscount.count += 1;
            saleDiscount.amount += discount.amount;
        });
    });

    const totalCount = saleDiscounts.reduce((previous, current) => previous + current.count, 0);
    const totalAmount = saleDiscounts.reduce((previous, current) => previous + current.amount, 0);

    arraySort(saleDiscounts, "type");

    const columnDefinitions = [];
    columnDefinitions.push(new ColumnDefinition(50));
    columnDefinitions.push(new ColumnDefinition(25, columnAlignments.alignRight));
    columnDefinitions.push(new ColumnDefinition(25, columnAlignments.alignRight));

    const tableData = new TableData("saleDiscounts", title, ...columnDefinitions);

    tableData.setHeaderRow("Discount Type", "Count", "Amount");
    tableData.setFooterRow("Total", totalCount.toString(), formatter.convertToAmountFormat(totalAmount));

    saleDiscounts.forEach(sale => tableData.addDataRow(sale.type, sale.count.toString(), formatter.convertToAmountFormat(sale.amount)));

    return tableData;
}

export function getSaleChargesTableData(sales, templateElement)
{
    // Overwrite title from template
    const title = templateElement.title || "Sale Charges";

    const saleCharges = [];

    sales.forEach(sale =>
    {
        if (sale.charges.length === 0) return;

        sale.charges.forEach(charge =>
        {
            let saleCharge = saleCharges.find(x => x.type === charge.type);

            if (saleCharge == null) 
            {
                saleCharge = { type: charge.type, count: 0, amount: 0.0, vat: 0.0 };
                saleCharges.push(saleCharge);
            }

            saleCharge.count += 1;
            saleCharge.amount += charge.amount;
            saleCharge.vat += charge.vat;
        });
    });

    const totalCount = saleCharges.reduce((previous, current) => previous + current.count, 0);
    const totalAmount = saleCharges.reduce((previous, current) => previous + current.amount, 0);
    const totalVat = saleCharges.reduce((previous, current) => previous + current.vat, 0);

    arraySort(saleCharges, "type");

    const columnDefinitions = [];
    columnDefinitions.push(new ColumnDefinition(40));
    columnDefinitions.push(new ColumnDefinition(10, columnAlignments.alignRight));
    columnDefinitions.push(new ColumnDefinition(25, columnAlignments.alignRight));
    columnDefinitions.push(new ColumnDefinition(25, columnAlignments.alignRight));

    const tableData = new TableData("saleCharges", title, ...columnDefinitions);

    tableData.setHeaderRow("Charge Type", "Count", "Sale Amt", "Vat amt");
    tableData.setFooterRow("Total", totalCount.toString(), formatter.convertToAmountFormat(totalAmount), formatter.convertToAmountFormat(totalVat));

    saleCharges.forEach(sale => tableData.addDataRow(sale.type, sale.count.toString(), formatter.convertToAmountFormat(sale.amount), formatter.convertToAmountFormat(sale.vat)));

    return tableData;
}

export function getSaleRefundsTableData(sales, templateElement)
{
    // Overwrite title from template
    const title = templateElement.title || "Sale Refunds";

    const saleRefunds = [];

    sales.forEach(sale =>
    {
        if (!sale.refunds || sale.refunds.length === 0) return;

        sale.refunds.forEach(refund => 
        {
            let saleRefund = saleRefunds.find(x => x.refundCode === refund.refundCode);

            if (saleRefund == null) 
            {
                saleRefund = { refundCode: refund.refundCode, count: 0, amount: 0.0 };
                saleRefunds.push(saleRefund);
            }

            saleRefund.count += 1;
            saleRefund.amount += refund.refundAmount;
        });
    });

    const totalCount = saleRefunds.reduce((previous, current) => previous + current.count, 0);
    const totalAmount = saleRefunds.reduce((previous, current) => previous + current.amount, 0);

    arraySort(saleRefunds, "refundCode");

    const columnDefinitions = [];
    columnDefinitions.push(new ColumnDefinition(50));
    columnDefinitions.push(new ColumnDefinition(25, columnAlignments.alignRight));
    columnDefinitions.push(new ColumnDefinition(25, columnAlignments.alignRight));

    const tableData = new TableData("saleRefunds", title, ...columnDefinitions);

    tableData.setHeaderRow("Refund Code", "Count", "Amount");
    tableData.setFooterRow("Total", totalCount.toString(), formatter.convertToAmountFormat(totalAmount));

    saleRefunds.forEach(refund => tableData.addDataRow(refund.refundCode, refund.count.toString(), formatter.convertToAmountFormat(refund.amount)));

    return tableData;
}

export function getSaleCancellationTableData(cancelledSales, templateElement)
{
    // Overwrite title from template
    const title = templateElement.title || "Sale Cancellations";

    const saleCancellations = [];

    cancelledSales.forEach(sale =>
    {
        if (!sale.isCancelled) return;

        let cancellation = saleCancellations.find(x => x.cancellationReason === sale.cancellationReason);

        if (cancellation == null) 
        {
            cancellation = { cancellationReason: sale.cancellationReason, count: 0, amount: 0.0 };
            saleCancellations.push(cancellation);
        }

        cancellation.count++;
        cancellation.amount += saleCalculator.calculateTotalSalePrice(sale);
    });

    const totalCount = saleCancellations.reduce((previous, current) => previous + current.count, 0);
    const totalAmount = saleCancellations.reduce((previous, current) => previous + current.amount, 0);

    const columnDefinitions = [];
    columnDefinitions.push(new ColumnDefinition(50));
    columnDefinitions.push(new ColumnDefinition(25, columnAlignments.alignRight));
    columnDefinitions.push(new ColumnDefinition(25, columnAlignments.alignRight));

    const tableData = new TableData("saleCancellations", title, ...columnDefinitions);

    tableData.setHeaderRow("Cancellation Code", "Count", "Amount");
    tableData.setFooterRow("Total", totalCount.toString(), formatter.convertToAmountFormat(totalAmount));

    saleCancellations.forEach(cancellation => tableData.addDataRow(
        cancellation.cancellationReason, cancellation.count.toString(), formatter.convertToAmountFormat(cancellation.amount)));

    return tableData;
}

export function getSaleVatTableData(sales, templateElement)
{
    // Overwrite title from template
    const title = templateElement.title || "Sales VAT";

    const isVatInclusive = localizationUtils.isVatInclusive();

    const saleVatPercentages = [];

    sales.forEach(sale =>
    {
        (function getSaleItemsVat(saleItems)
        {
            for (const saleItem of saleItems) 
            {
                const saleItemVatData = getSaleItemVatData(saleItem, isVatInclusive);

                if (saleItemVatData != null)
                {
                    let saleVatPercentage = saleVatPercentages.find(x => x.vatPercentage === saleItemVatData.vatPercentage);

                    if (saleVatPercentage == null)
                    {
                        saleVatPercentage = { vatPercentage: saleItemVatData.vatPercentage, count: 0, saleAmount: 0, vatAmount: 0 }
                        saleVatPercentages.push(saleVatPercentage);
                    }

                    saleVatPercentage.count += saleItemVatData.quantity;
                    saleVatPercentage.saleAmount += saleItemVatData.saleAmount;
                    saleVatPercentage.vatAmount += saleItemVatData.vatAmount;
                }

                if (saleItem.saleItems.length > 0) 
                {
                    getSaleItemsVat(saleItem.saleItems);    // recursion
                }
            }
        })(sale.saleItems); // IIF
    });

    const totalCount = saleVatPercentages.reduce((previous, current) => previous + current.count, 0);
    const totalSaleAmount = saleVatPercentages.reduce((previous, current) => previous + current.saleAmount, 0);
    const totalVatAmount = saleVatPercentages.reduce((previous, current) => previous + current.vatAmount, 0);

    arraySort(saleVatPercentages, (p1, p2) =>
    {
        // Sort by numeric value of vatPercentage in ascending order,
        // "Others" if exists to be the last item

        const v1 = parseFloat(p1.vatPercentage);
        if (isNaN(v1)) return 1;

        const v2 = parseFloat(p2.vatPercentage);
        if (isNaN(v2)) return -1;

        return v1 - v2;
    });

    const columnDefinitions = [];
    columnDefinitions.push(new ColumnDefinition(25));
    columnDefinitions.push(new ColumnDefinition(25, columnAlignments.alignRight));
    columnDefinitions.push(new ColumnDefinition(25, columnAlignments.alignRight));
    columnDefinitions.push(new ColumnDefinition(25, columnAlignments.alignRight));

    const tableData = new TableData("saleVats", title, ...columnDefinitions);

    tableData.setHeaderRow("Vat %", "Item Count", "Sale Amount", "Vat Amount");
    tableData.setFooterRow(
        // `${totalSaleAmount < constants.numericThresholdValue ? 'Total' : `${(formatter.roundUp(totalVatAmount / totalSaleAmount, 2) * 100).toFixed(2)}%`}`,
        "Total",
        totalCount.toString(),
        formatter.convertToAmountFormat(totalSaleAmount),
        formatter.convertToAmountFormat(totalVatAmount));

    saleVatPercentages.forEach(sale =>
        tableData.addDataRow(sale.vatPercentage, sale.count.toString(), formatter.convertToAmountFormat(sale.saleAmount), formatter.convertToAmountFormat(sale.vatAmount)));

    return tableData;
}

export function getSalesByMenuCategoryTableData(sales, templateElement)
{
    // Overwrite title from template
    const title = templateElement.title || "Menu Categories Summary";

    const categorySales = [];

    sales.forEach(sale =>
    {
        sale.saleItems.forEach(saleItem => 
        {
            const category = saleItem.category || "N/A";
            const quantity = saleItem.quantity;
            const amount = saleCalculator.calculateTotalItemPrice(saleItem);

            let categorySale = categorySales.find(x => x.category === category);

            if (categorySale == null) 
            {
                categorySale = { category: category, count: 0, amount: 0 };
                categorySales.push(categorySale);
            }

            categorySale.count += quantity;
            categorySale.amount += amount;
        });
    });

    const totalCount = categorySales.reduce((previous, current) => previous + current.count, 0);
    const totalAmount = categorySales.reduce((previous, current) => previous + current.amount, 0);

    arraySort(categorySales, "category");

    const columnDefinitions = [];
    columnDefinitions.push(new ColumnDefinition(50));
    columnDefinitions.push(new ColumnDefinition(25, columnAlignments.alignRight));
    columnDefinitions.push(new ColumnDefinition(25, columnAlignments.alignRight));

    const tableData = new TableData("salesByMenuCategory", title, ...columnDefinitions);

    tableData.setHeaderRow("Menu Category", "Count", "Amount");
    tableData.setFooterRow("Total", totalCount.toString(), formatter.convertToAmountFormat(totalAmount));

    categorySales.forEach(sale => tableData.addDataRow(sale.category, sale.count.toString(), formatter.convertToAmountFormat(sale.amount)));

    return tableData;
}

export function getFloatSessionTableData(floatSession, index, allSales, templateElement)
{
    let cashIn, cashOut;

    const dateTimeFormat = "dddd, hh:mm a";
    const entryType = constants.floatManagementJournalEntryType;
    const entrySubType = constants.floatManagementJournalEntrySubType;

    const sales = allSales.filter(sale => sale.floatSessionId === floatSession.id);

    const startDateTime = dateUtil.formatDate(dateUtil.convertToLocalStandard(floatSession.startDateTime), dateTimeFormat);
    const startedByStaff = staffUtility.getStaffById(floatSession.startedBy);

    const endDateTime = floatSession.endDateTime && dateUtil.formatDate(dateUtil.convertToLocalStandard(floatSession.endDateTime), dateTimeFormat);
    const endedByStaff = floatSession.endedBy && staffUtility.getStaffById(floatSession.endedBy);

    const openingCash = floatSession.journals
        .filter(journal => journal.type === entryType.Deposit && journal.subType === entrySubType.OpeningBalance && !journal.isVoided)
        .reduce((previous, current) => previous + current["amount"], 0);

    const cashExpense = floatSession.journals
        .filter(journal => journal.type === entryType.Withdraw && journal.subType === entrySubType.Expense && !journal.isVoided)
        .reduce((previous, current) => previous + current["amount"], 0);

    const cashSales = sales
        .flatMap(sale => sale.receiptLines.filter(receiptLine => receiptLine.paymentMethod === "Cash" && receiptLine.type === "Charge"))
        .reduce((previous, current) => previous + current.amount, 0);

    const cashRefunds = sales
        .flatMap(sale => sale.receiptLines.filter(receiptLine => receiptLine.paymentMethod === "Cash" && receiptLine.type === "Refund"))
        .reduce((previous, current) => previous + current.amount, 0);

    // Net cash transfer
    cashOut = floatSession.journals
        .filter(journal => journal.type === entryType.Withdraw && journal.subType === entrySubType.TransferToSafe && !journal.isVoided)
        .reduce((previous, current) => previous + current["amount"], 0);

    cashIn = floatSession.journals
        .filter(journal => journal.type === entryType.Deposit && journal.subType === entrySubType.TransferFromSafe && !journal.isVoided)
        .reduce((previous, current) => previous + current["amount"], 0);

    // cashOut perspective
    const netCashTransfer = cashOut - cashIn;

    // Net cash driver change (cashOut perspective)
    cashOut = floatSession.journals
        .filter(journal => journal.type === entryType.Withdraw && journal.subType === entrySubType.ChangeGiven && !journal.isVoided)
        .reduce((previous, current) => previous + current["amount"], 0);

    cashIn = floatSession.journals
        .filter(journal => journal.type === entryType.Deposit && journal.subType === entrySubType.ChangeReturned && !journal.isVoided)
        .reduce((previous, current) => previous + current["amount"], 0);

    // cashOut perspective
    const netCashChange = cashOut - cashIn;

    const cashBalance = openingCash + cashSales - cashRefunds - cashExpense - netCashTransfer - netCashChange;

    // Draw table data
    // Overwrite title from template

    const floatIdentifier = floatSession.floatIdentifier ? `(Float ID: ${floatSession.floatIdentifier}) ` : "";
    const title = `${templateElement.title || "Float Session"}: ${floatIdentifier} ${startDateTime}`;

    const columnDefinitions = [];
    columnDefinitions.push(new ColumnDefinition(70));
    columnDefinitions.push(new ColumnDefinition(30, columnAlignments.alignRight));

    const tableData = new TableData("floatSession", title, ...columnDefinitions);

    tableData.addDataRow("Started at", startDateTime);
    tableData.addDataRow("Started by", startedByStaff ? `${startedByStaff.firstName} ${startedByStaff.lastName}` : "N/A");

    tableData.addDataRow("Closed at", endDateTime || "---");
    tableData.addDataRow("Closed by", endedByStaff ? `${endedByStaff.firstName} ${endedByStaff.lastName}` : "---");

    tableData.addDataRow("Opening Cash", formatter.convertToAmountFormat(openingCash));
    tableData.addDataRow("Cash Expenses", formatter.convertToAmountFormat(cashExpense));
    tableData.addDataRow("Cash Transfers *", formatter.convertToAmountFormat(netCashTransfer));
    tableData.addDataRow("Cash Changed *", formatter.convertToAmountFormat(netCashChange));
    tableData.addDataRow("Cash Sales **", formatter.convertToAmountFormat(cashSales));
    tableData.addDataRow("Cash Refunds", formatter.convertToAmountFormat(cashRefunds));

    tableData.setFooterRow("Closing Balance", formatter.convertToAmountFormat(cashBalance));

    tableData.footNotes = [
        "* Read from net cash out perspective",
        "** Only sales belonging to the selected date are included in the float session"
    ];

    return tableData;
}

export function getFloatSessionJournalTableData(floatSession, index, allSales, templateElement)
{
    let tableRow, totalAmount;

    const entryType = constants.floatManagementJournalEntryType;
    const entrySubType = constants.floatManagementJournalEntrySubType;

    const journals = floatSession.journals.filter(entry => !entry.isVoided);

    // transferEntries
    const transferEntries = { deposits: 0, withdrawals: 0 };

    transferEntries.deposits += journals
        .filter(entry => entry.type === entry.subType === entrySubType.TransferFromSafe)
        .reduce((previous, current) => previous + current.amount, 0);

    transferEntries.withdrawals += journals
        .filter(entry => entry.type === entry.subType === entrySubType.TransferToSafe)
        .reduce((previous, current) => previous + current.amount, 0);

    // driverEntries
    const driverEntries = [];

    journals
        .filter(entry => entry.subType === entrySubType.ChangeGiven || entry.subType === entrySubType.ChangeReturned)
        .forEach(entry => 
        {
            const driverId = entry.subTypeReferenceId;
            const amount = entry.subType === entrySubType.ChangeGiven ? entry.amount : -entry.amount;

            let driverEntry = driverEntries.find(entry => entry.driverId === driverId);
            if (driverEntry == null) 
            {
                driverEntry = { driverId: driverId, amount: 0.0 };
                driverEntries.push(driverEntry);
            }

            driverEntry.amount += amount;
        });

    // expenseEntries
    const expenseEntries = [];

    journals
        .filter(entry => entry.subType === entrySubType.Expense)
        .forEach(entry => 
        {
            const creationDateTime = entry.creationDateTime;
            const createdByStaffId = entry.createdByStaffId;
            const subCategoryId = entry.subTypeReferenceId;
            const amount = entry.amount;

            expenseEntries.push({ creationDateTime, createdByStaffId, subCategoryId, amount });
        });

    // Draw table data
    // Overwrite title from template

    const dateTimeFormat = "dddd, hh:mm a";
    const startDateTime = dateUtil.formatDate(dateUtil.convertToLocalStandard(floatSession.startDateTime), dateTimeFormat);

    const floatIdentifier = floatSession.floatIdentifier ? `(Float ID: ${floatSession.floatIdentifier}) ` : "";
    const title = `${templateElement.title || "Float Session Journal"}: ${floatIdentifier} ${startDateTime}`;

    const columnDefinitions = [];
    columnDefinitions.push(new ColumnDefinition(25));
    columnDefinitions.push(new ColumnDefinition(50));
    columnDefinitions.push(new ColumnDefinition(25, columnAlignments.alignRight));

    const tableData = new TableData("floatSessionJournal", title, ...columnDefinitions);

    // Expense entries

    if (expenseEntries.length > 0) 
    {
        totalAmount = 0;

        expenseEntries.forEach(entry => 
        {
            const createdAt = dateUtil.formatDate(dateUtil.convertToLocalStandard(entry.creationDateTime), "hh:mm a");
            const createdBy = staffUtility.getStaffCompleteName(staffUtility.getStaffById(entry.createdByStaffId));
            const subCategory = accountsUtils.findSubCategory(entry.subCategoryId);

            totalAmount += entry.amount;

            tableData.addDataRow("Expense", `${subCategory.name}\nBy:${createdBy} @${createdAt}`, formatter.convertToAmountFormat(entry.amount));
        });

        tableRow = tableData.addDataRow("", "Total", formatter.convertToAmountFormat(totalAmount));
        tableRow.afterBlankRow = true;
        tableRow.columns[1].isBold = true;
        tableRow.columns[1].alignment = tableConstants.columnAlignments.alignRight;
        tableRow.columns[2].isBold = true;
    }

    // Driver entries

    if (driverEntries.length > 0) 
    {
        totalAmount = 0;

        driverEntries.forEach(entry => 
        {
            const driverName = staffUtility.getStaffCompleteName(staffUtility.getStaffById(entry.driverId));

            totalAmount += entry.amount;

            tableData.addDataRow("Driver", driverName, formatter.convertToAmountFormat(entry.amount));
        });

        tableRow = tableData.addDataRow("", "Total", formatter.convertToAmountFormat(totalAmount));
        tableRow.afterBlankRow = true;
        tableRow.columns[1].isBold = true;
        tableRow.columns[1].alignment = tableConstants.columnAlignments.alignRight;
        tableRow.columns[2].isBold = true;
    }

    // Transfer entries

    tableData.addDataRow("Deposits", "Till Deposits", formatter.convertToAmountFormat(transferEntries.deposits));
    tableData.addDataRow("Withdrawals", "Till Withdrawals", formatter.convertToAmountFormat(transferEntries.withdrawals));

    totalAmount = transferEntries.deposits - transferEntries.withdrawals;

    tableRow = tableData.addDataRow("", "Total", formatter.convertToAmountFormat(totalAmount));
    tableRow.columns[1].isBold = true;
    tableRow.columns[1].alignment = tableConstants.columnAlignments.alignRight;
    tableRow.columns[2].isBold = true;

    return tableData;
}

function getSaleItemVatData(saleItem, isVatInclusive)
{
    let vatPercentage, vatAmount;

    if (saleItem.unitPrice === 0 && saleItem.vat === 0)
    {
        return null;
    }

    if (saleItem.unitPrice === 0 && saleItem.vat > 0) 
    {
        return {
            vatPercentage: "Others",
            quantity: saleItem.quantity,
            saleAmount: 0,
            vatAmount: saleItem.vat * saleItem.quantity
        };
    }

    const unitVat = saleItem.vat;
    const quantity = saleItem.quantity;
    const unitPrice = isVatInclusive ? saleItem.unitPrice - unitVat : saleItem.unitPrice;

    const saleAmount = unitPrice * quantity;

    if (unitVat < constants.numericThresholdValue)
    {
        vatPercentage = 0;
        vatAmount = 0;
    }
    else
    {
        vatPercentage = formatter.roundUp(unitVat / unitPrice * 100, 2);
        vatAmount = quantity * unitVat;

        const vatBands = localizationUtils.getOperativeVatBands();

        if (vatBands.length > 0)
        {
            vatPercentage = vatBands.reduce((a, b) => Math.abs(b - vatPercentage) < Math.abs(a - vatPercentage) ? b : a);
        }
    }

    return { vatPercentage: `${vatPercentage.toFixed(2)} %`, quantity, saleAmount, vatAmount };
}